import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Sparkles } from "lucide-react";
import { API } from "../lib/api";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";

const TABS = [
  ["trending", "Trending"], ["foryou", "For You"], ["picks", "Sakhi Picks"],
  ["looks", "Looks"], ["new", "New"], ["under99", "Under ₹99"], ["club", "Club Favourites"],
];

function ProductGrid({ items, empty }) {
  if (!items) return <GridSkeleton />;
  if (items.length === 0) return <Empty msg={empty} />;
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-6" data-testid="discover-products">{items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>;
}

function LooksGrid({ posts }) {
  if (!posts) return <GridSkeleton />;
  if (posts.length === 0) return <Empty msg="Be the first Sakhi to post a look ✨" cta />;
  return (
    <>
      <div className="flex justify-end mt-4"><Link to="/sakhi-wall" className="text-sm font-semibold text-[#6A1B29]" data-testid="discover-see-wall">See the full Sakhi Wall →</Link></div>
      <div className="columns-2 md:columns-4 gap-4 mt-3 [column-fill:_balance]" data-testid="discover-looks">
      {posts.map((p) => (
        <Link key={p.id} to={p.username ? `/sakhi/${p.username}` : "/community"} className="mb-4 block break-inside-avoid group" data-testid={`look-${p.id}`}>
          <div className="relative overflow-hidden rounded-2xl bg-stone-100">
            {p.media?.[0] && <img src={p.media[0]} alt="" loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3 text-white">
              <p className="text-xs font-semibold truncate">{p.user_name || "A Sakhi"}</p>
              {p.caption && <p className="text-[11px] text-white/80 line-clamp-1">{p.caption}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-stone-400 text-xs px-1">
            <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {p.like_count || 0}</span>
            <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> {p.comment_count || 0}</span>
          </div>
        </Link>
      ))}
    </div>
    </>
  );
}

function GridSkeleton() {
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-stone-100 animate-pulse" />)}</div>;
}
function Empty({ msg, cta }) {
  return (
    <div className="mt-10 bg-white border border-stone-200 rounded-3xl p-12 text-center" data-testid="discover-empty">
      <Sparkles className="w-8 h-8 text-[#6A1B29] mx-auto" />
      <p className="font-display text-xl text-stone-800 mt-3">{msg}</p>
      {cta && <Link to="/community/share" className="inline-block mt-5 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-3 rounded-full text-sm" data-testid="discover-empty-cta">Share your look</Link>}
    </div>
  );
}

export default function Discover() {
  const [tab, setTab] = useState("trending");
  const [cache, setCache] = useState({});

  useEffect(() => {
    if (cache[tab]) return;
    const done = (data) => setCache((c) => ({ ...c, [tab]: data }));
    if (tab === "looks") { API.get("/community/posts?type=look&limit=40").then((r) => done(r.data)).catch(() => done([])); return; }
    const url = {
      trending: "/products?trending=true&limit=12",
      foryou: "/products?limit=12",
      picks: "/products?featured=true&limit=12",
      new: "/products?new_arrival=true&limit=12",
      under99: "/products?limit=40",
      club: "/products?limit=40",
    }[tab];
    API.get(url).then((r) => {
      let items = r.data.items || [];
      if (tab === "under99") items = items.filter((p) => p.selling_price <= 99).slice(0, 12);
      if (tab === "club") items = items.filter((p) => p.club_price != null || p.is_club_drop).slice(0, 12);
      done(items);
    }).catch(() => done([]));
  }, [tab, cache]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="discover-page">
      <Seo title="Discover | Suvikka Sakhi" description="Discover trending beauty, fashion and jewellery finds, Sakhi Picks and real community looks on Suvikka." canonicalPath="/discover" />
      <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#6A1B29]">Suvikka</p>
      <h1 className="font-display text-3xl sm:text-5xl font-semibold text-stone-900 mt-2">Discover ✨</h1>
      <p className="text-stone-500 mt-2">Finds she'll love, picked by the community.</p>
      <div className="flex gap-2 mt-6 overflow-x-auto pb-2 -mx-1 px-1" data-testid="discover-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`discover-tab-${k}`}
            className={`shrink-0 text-sm font-medium px-4 py-2 rounded-full transition-colors ${tab === k ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-stone-900"}`}>{l}</button>
        ))}
      </div>
      {tab === "looks" ? <LooksGrid posts={cache.looks} /> : (
        <ProductGrid items={cache[tab]} empty={tab === "under99" ? "No finds under ₹99 yet — check back soon ✨" : tab === "club" ? "Club favourites are coming soon 💗" : "Nothing here yet — check back soon ✨"} />
      )}
    </div>
  );
}
